import React from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import routeMetadata from "./routeMetadata.json";
import faqStructuredData from "./faqStructuredData";
import { resolveRuleTemplates } from "../data/releasedRules";

const PRIMARY_ORIGIN = "https://student-loan-calculator.co.uk";
const DEFAULT_IMAGE = `${PRIMARY_ORIGIN}/logo512.png`;

const routeMap = routeMetadata.reduce((memo, route) => {
  memo[route.path] = route;
  return memo;
}, {});

const normalizePath = (pathname) => {
  if (!pathname || pathname === "/") {
    return "/";
  }

  return pathname.replace(/\/$/, "");
};

const buildCanonicalUrl = (path) =>
  `${PRIMARY_ORIGIN}${path === "/" ? "/" : path}`;

const buildBreadcrumbSchema = (route) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: buildCanonicalUrl("/"),
    },
    {
      "@type": "ListItem",
      position: 2,
      name: route.label,
      item: buildCanonicalUrl(route.path),
    },
  ],
});

const buildPrimarySchema = (route, canonicalUrl) => {
  if (route.schemaType === "WebApplication") {
    return {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: route.label,
      url: canonicalUrl,
      description: route.description,
      applicationCategory: "FinanceApplication",
      operatingSystem: "All",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "GBP",
      },
    };
  }

  if (route.schemaType === "FAQPage") {
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqStructuredData.map((entry) => ({
        "@type": "Question",
        name: entry.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: entry.answer,
        },
      })),
    };
  }

  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: route.label,
    url: canonicalUrl,
    description: route.description,
  };
};

function RouteSeo() {
  const location = useLocation();
  const currentPath = normalizePath(location.pathname);
  const route = routeMap[currentPath];

  if (!route) {
    return (
      <Helmet>
        <title>404 - Page Not Found - The UK Student Loan Calculator</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
    );
  }

  const canonicalUrl = buildCanonicalUrl(route.path);
  const title = resolveRuleTemplates(route.title);
  const description = resolveRuleTemplates(route.description);
  const robotsContent = route.indexable
    ? "index, follow, max-image-preview:large"
    : "noindex, follow";
  const resolvedRoute = { ...route, title, description };
  const primarySchema = buildPrimarySchema(resolvedRoute, canonicalUrl);
  const breadcrumbSchema =
    route.path === "/" ? null : buildBreadcrumbSchema(route);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      <meta name="robots" content={robotsContent} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={DEFAULT_IMAGE} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={DEFAULT_IMAGE} />
      <script type="application/ld+json">
        {JSON.stringify(primarySchema)}
      </script>
      {breadcrumbSchema ? (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      ) : null}
    </Helmet>
  );
}

export default RouteSeo;
